import { getMemberList, type TenantMemberListItem } from '@/services/bff';

let cachedMemberList: TenantMemberListItem[] | null = null;
let cachedMemberMap: Map<string, TenantMemberListItem> | null = null;
let fetchingPromise: Promise<TenantMemberListItem[]> | null = null;

export const buildMemberIndexMap = (
  members: TenantMemberListItem[],
): Map<string, TenantMemberListItem> => {
  const map = new Map<string, TenantMemberListItem>();
  members.forEach((member) => {
    const keys = [
      member.id,
      member.memberUuid,
      member.userUuid,
      member.userId,
    ].filter(Boolean) as string[];

    keys.forEach((key) => {
      const trimmed = key.trim();
      if (trimmed && !map.has(trimmed)) {
        map.set(trimmed, member);
      }
    });
  });
  return map;
};

export const fetchAndCacheOrgMembers = async (): Promise<TenantMemberListItem[]> => {
  if (cachedMemberList && cachedMemberList.length > 0) {
    return cachedMemberList;
  }
  if (fetchingPromise) {
    return fetchingPromise;
  }

  fetchingPromise = (async () => {
    try {
      const result = await getMemberList({ pageNo: 1, pageSize: 500 });
      const items = (result?.items || []).filter((item) => item.id || item.memberUuid);
      cachedMemberList = items;
      cachedMemberMap = buildMemberIndexMap(items);
      return items;
    } catch (error) {
      console.warn('Failed to prefetch organization members:', error);
      return [];
    } finally {
      fetchingPromise = null;
    }
  })();

  return fetchingPromise;
};

export const getCachedOrgMembers = (): TenantMemberListItem[] => {
  return cachedMemberList || [];
};

export const getCachedMemberById = (
  idOrUuid?: string | null,
): TenantMemberListItem | undefined => {
  if (!idOrUuid) return undefined;
  const key = idOrUuid.trim();
  return cachedMemberMap?.get(key);
};

export const getCachedMemberName = (
  idOrUuid?: string | null,
): string => {
  if (!idOrUuid) return '';
  const member = getCachedMemberById(idOrUuid);
  return member?.name?.trim() || member?.nickname?.trim() || '';
};

export const clearOrgMemberCache = () => {
  cachedMemberList = null;
  cachedMemberMap = null;
  fetchingPromise = null;
};
